import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Mail, HelpCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const Login = ({ setUser }) => {
  const navigate = useNavigate();
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Check if already authenticated
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (response.ok) {
          const user = await response.json();
          setUser(user);
          navigate('/');
        }
      } catch (error) {
        localStorage.removeItem('token');
      }
    };
    checkExistingSession();
  }, [navigate, setUser]);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }

    if (!password.trim()) {
      toast.error('Please enter your password');
      return;
    }

    // Allow @nobroker.in and test accounts (@test.com for CRM_SUPPORT testing)
    if (!email.endsWith('@nobroker.in') && !email.endsWith('@test.com')) {
      toast.error('Only @nobroker.in accounts are allowed');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: email.toLowerCase().trim(),
          password: password
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        toast.error(data.detail || 'Login failed. Please try again.');
        setIsLoading(false);
        return;
      }
      
      // Store token and user
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
      toast.success(`Welcome, ${data.user.rep_name || data.user.full_name || data.user.email}!`);
      navigate('/');
      
    } catch (error) {
      toast.error('Login failed. Please try again.');
    }
    
    setIsLoading(false);
  };

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center p-4" 
      style={{
        background: 'linear-gradient(135deg, #2E1E5F 0%, #5B3DF5 100%)',
      }}
    >
      {/* Main Content */}
      <div className="w-full max-w-md">
        {/* Branding with Purple Logo */}
        <div className="text-center mb-10">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <span className="text-white font-bold text-2xl">PX</span>
            </div>
          </div>
          <h1 className="text-5xl font-bold text-white uppercase tracking-tight mb-3">
            PIPELINE X
          </h1>
          <p className="text-lg text-slate-300 font-medium">
            Primary Sales Operating System
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8">
          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Work Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@nobroker.in"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 rounded-xl text-base"
                autoComplete="email"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 rounded-xl text-base pr-12"
                  autoComplete="current-password"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 bg-[#5B3DF5] hover:bg-[#4A2FE0] rounded-xl font-semibold text-base"
              data-testid="login-submit-btn"
            >
              {isLoading ? (
                <><Loader2 size={18} className="animate-spin mr-2" /> Signing in...</>
              ) : (
                'Sign In'
              )}
            </Button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-400">or</span>
              </div>
            </div>

            {/* Google SSO Button */}
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                const currentUrl = window.location.origin;
                const callbackUrl = `${currentUrl}/auth/callback`;
                const emergentAuthUrl = `https://accounts.emergentagent.com/oauth/google?callback_url=${encodeURIComponent(callbackUrl)}`;
                window.location.href = emergentAuthUrl;
              }}
              className="w-full h-12 rounded-xl font-medium text-base border-slate-200 hover:bg-slate-50"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <p className="text-center text-sm text-slate-500">
              Only <span className="font-semibold text-slate-700">@nobroker.in</span> accounts allowed
            </p>

            {/* Contact Admin Button */}
            <div className="pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowContactDialog(true)}
                className="w-full h-10 text-slate-500 hover:text-slate-700 hover:bg-slate-100 text-sm rounded-xl"
              >
                <HelpCircle size={16} className="mr-2" />
                Forgot password? Contact Admin
              </Button>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 space-y-1">
          <p className="text-sm font-semibold text-white/90">Pipeline X v1.0</p>
          <p className="text-xs text-slate-400">Built for High Performance Teams</p>
          <p className="text-xs text-slate-500">Internal Use Only</p>
        </div>
      </div>

      {/* Contact Admin Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Contact Admin</DialogTitle>
            <DialogDescription className="text-slate-600">
              To reset your password or get access to Pipeline X, please contact your administrator.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
              <Mail size={20} className="text-slate-500" />
              <div>
                <p className="text-sm text-slate-500">Email</p>
                <p className="font-medium text-slate-800">admin@nobroker.in</p>
              </div>
            </div>
            <p className="text-sm text-slate-500">
              Please include your name, team, and city in your request.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
